import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Switch,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import {
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { Routes } from "../constant/api_url";

const POST_TYPE_NAMES = {
  1: "President",
  2: "Vice-President",
  3: "ST",
  4: "LVH"
};

export default function CascadeConfirmModal({
  open,
  onClose,
  onConfirm,
  currentHolder,
  newHolder,
  postTypeName,
  coordinatorCount,
  roleCount,
  pendingChangedPostTypes,
  coordinatorCounts,
  removedPostTypeIds = [],
  loading
}) {
  const [removeCoordinatorsMap, setRemoveCoordinatorsMap] = useState({});

  useEffect(() => {
    if (!open) {
      setRemoveCoordinatorsMap({});
    }
  }, [open]);

  const isMultiMode = Array.isArray(pendingChangedPostTypes) && pendingChangedPostTypes.length > 0;
  const isRemovedPost = (postTypeId) => (removedPostTypeIds || []).includes(postTypeId);
  const allRemovals = isMultiMode && pendingChangedPostTypes.every((pt) => isRemovedPost(pt.id));
  const anyRemoval = isMultiMode && pendingChangedPostTypes.some((pt) => isRemovedPost(pt.id));

  const handleToggle = (postTypeId, checked) => {
    setRemoveCoordinatorsMap((prev) => ({ ...prev, [postTypeId]: checked }));
  };

  const handleConfirm = () => {
    if (isMultiMode) {
      onConfirm(removeCoordinatorsMap);
    } else {
      onConfirm(removeCoordinatorsMap[0] || false);
    }
  };

  const handleClose = () => {
    setRemoveCoordinatorsMap({});
    onClose();
  };

  const getPhotoUrl = (photo) => {
    if (photo) return `${Routes.API_HOSTNAME}images/member/${photo}`;
    return null;
  };

  const getTotalCoordinatorCount = () => {
    if (isMultiMode) {
      return pendingChangedPostTypes.reduce(
        (sum, pt) => sum + (coordinatorCounts?.[pt.id] || 0), 0
      );
    }
    return coordinatorCount || 0;
  };

  const totalCoords = getTotalCoordinatorCount();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxWidth: isMultiMode ? 560 : 480 }
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 2 }}>
        <SwapHorizIcon color="primary" />
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          {isMultiMode && allRemovals ? "Remove Post-Holder" : "Change Post-Holder"}{isMultiMode ? "s" : ""}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Single mode: holder comparison */}
        {!isMultiMode && currentHolder && (
          <>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You are about to change the <strong>{postTypeName}</strong> holder.
            </Typography>
            <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2.5, mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current Holder</Typography>
                  <Avatar
                    src={getPhotoUrl(currentHolder?.photo)}
                    sx={{ width: 48, height: 48, border: "2px solid", borderColor: "grey.300" }}
                  >
                    {(currentHolder?.name || "U").charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
                    {currentHolder?.name || "Not assigned"}
                  </Typography>
                </Stack>
                <SwapHorizIcon sx={{ color: "grey.400", fontSize: 28 }} />
                <Stack alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">New Holder</Typography>
                  <Avatar
                    src={getPhotoUrl(newHolder?.photo)}
                    sx={{ width: 48, height: 48, border: "2px solid", borderColor: "primary.light" }}
                  >
                    {(newHolder?.name || "N").charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
                    {newHolder?.name || "Not selected"}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </>
        )}

        {/* Multi mode: changed post types list */}
        {isMultiMode && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {allRemovals
              ? "The following post holders are being removed. Their coordinators will stay and appear under the next holder. Would you like to remove coordinators under these posts instead?"
              : anyRemoval
                ? "The following post holders have been changed or removed. Removed holders' coordinators will stay and appear under the next holder. Would you like to remove coordinators under these posts?"
                : "The following post holders have been changed. Would you like to remove coordinators under these posts?"}
          </Typography>
        )}

        {/* Coordinator Count Alert */}
        {totalCoords > 0 && (
          <Box sx={{ bgcolor: "warning.50", borderRadius: 2, p: 2, mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
            <WarningIcon sx={{ color: "warning.600", fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {isMultiMode
                ? <>A total of <strong>{totalCoords}</strong> coordinator(s) are assigned across the {allRemovals ? "vacated" : "changed"} post{pendingChangedPostTypes.length > 1 ? "s" : ""}.</>
                : <>This post type has <strong>{coordinatorCount}</strong> coordinator(s) assigned across <strong>{roleCount}</strong> role(s).</>
              }
            </Typography>
          </Box>
        )}

        {/* Cascade Toggle(s) */}
        {isMultiMode ? (
          <Stack spacing={2}>
            {pendingChangedPostTypes.map((pt) => {
              const count = coordinatorCounts?.[pt.id] || 0;
              const isChecked = !!removeCoordinatorsMap[pt.id];
              const removed = isRemovedPost(pt.id);
              return (
                <Box
                  key={pt.id}
                  sx={{
                    bgcolor: isChecked ? "error.50" : "background.paper",
                    border: "1px solid",
                    borderColor: isChecked ? "error.main" : "divider",
                    borderRadius: 2,
                    p: 2.5
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Switch
                      checked={isChecked}
                      onChange={(e) => handleToggle(pt.id, e.target.checked)}
                      color="error"
                      disabled={count === 0}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {POST_TYPE_NAMES[pt.id] || `Post Type ${pt.id}`}
                        {removed ? " — removing holder" : ""}
                        {count === 0 && " (no coordinators)"}
                      </Typography>
                      {isChecked && count > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          All {count} coordinator(s) for {POST_TYPE_NAMES[pt.id]} will be permanently removed.
                        </Typography>
                      )}
                      {!isChecked && removed && count > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                          Coordinators will stay and appear under the next {POST_TYPE_NAMES[pt.id]}.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{
            bgcolor: removeCoordinatorsMap[0] ? "error.50" : "background.paper",
            border: "1px solid",
            borderColor: removeCoordinatorsMap[0] ? "error.main" : "divider",
            borderRadius: 2,
            p: 2.5
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Switch
                checked={!!removeCoordinatorsMap[0]}
                onChange={(e) => handleToggle(0, e.target.checked)}
                color="error"
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Remove all coordinators</Typography>
                {!!removeCoordinatorsMap[0] && coordinatorCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    When enabled, all {coordinatorCount} coordinator(s) for {postTypeName} will be permanently removed.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
